import { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { DeepRequired, FieldErrorsImpl, useForm, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
  useRadioGroup,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { PaymentMethod } from '@stripe/stripe-js';

import { Logo } from '~/components/logo';
import { RadioCard } from '~/components/radio-card';
import { SectionCard } from '~/components/section-card';
import { SectionGroup } from '~/components/section-group';
import { StripePaymentMethod } from '~/components/stripe-payment-method';
import { setAuthCookie } from '~/features/auth/utils';
import { RecipientSlider } from '~/features/recipient-count';
import { useGetPrices } from '~/hooks/use-get-prices';
import { supabase } from '~/libs/supabase';
import { RootState } from '~/store';
import { BillingPeriod } from '~/types';

import { SignUpFormData, signUpFormDataSchema } from './types';
import { getMonthlyPrice } from './utils';

const defaultBilled = 'yearly';
const billingPeriods: BillingPeriod[] = ['yearly', 'monthly'];

export const SignUpForm: FC = () => {
  const router = useRouter();

  const toast = useToast();

  const { recipientCount } = useSelector((state: RootState) => state.recipientCount);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>();

  const { monthlyPrice, yearlyMonthlyPrice } = useGetPrices();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(signUpFormDataSchema),
    shouldUseNativeValidation: false,
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      billingPeriod: defaultBilled,
      recipientCount: 1,
      paymentMethod: paymentMethod?.id ? paymentMethod?.id : '',
    } as SignUpFormData,
  });

  // Synchronize paymentMethod state with RHF
  useEffect(() => {
    setValue('paymentMethod', paymentMethod?.id ? paymentMethod?.id : '', {
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // Synchronize recipientCount state with RHF
  useEffect(() => {
    setValue('recipientCount', recipientCount, {
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientCount]);

  const billingPeriod = watch('billingPeriod') as BillingPeriod;

  const onSubmit = async (values: SignUpFormData) => {
    const { user, session, error } = await supabase.auth.signUp({ email: values.email, password: values.password });

    if (error) {
      toast({
        title: 'An error occurred',
        description: error.message ? error.message : 'An error occurred, please try again',
        status: 'error',
        isClosable: true,
      });

      console.error(error);

      return;
    }

    if (!user) {
      throw new Error('User data not found in sign up');
    }

    await setAuthCookie(session, 'SIGNED_IN');

    try {
      const subscriptionResponse = await fetch('/api/create-subscription', {
        method: 'POST',
        body: JSON.stringify({
          email: values.email,
          paymentMethod: values.paymentMethod,
          billingPeriod: values.billingPeriod,
          recipientCount: values.recipientCount,
        }),
      });

      const subscription = await subscriptionResponse.json();

      if (!subscriptionResponse.ok) {
        throw new Error(subscription.message);
      }

      router.push('/dashboard');
    } catch (error) {
      toast({
        title: 'An error occurred',
        description: error ? `${error}` : 'An error occurred, please try again',
        status: 'error',
        isClosable: true,
      });

      console.error(error);
    }
  };

  const getCharge = () => {
    const price = getMonthlyPrice(billingPeriod, recipientCount, monthlyPrice, yearlyMonthlyPrice);

    if (billingPeriod === 'monthly') {
      return `$${price} monthly`;
    } else {
      return `$${price * 12} yearly`;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionCard
        title='Sign up'
        footerSlot={<SectionFooter isValid={isValid} isSubmitting={isSubmitting} charge={getCharge()} />}
      >
        <Stack spacing={8}>
          <CreateAccount register={register} errors={errors} />
          <PlanDetails setValue={setValue} errors={errors} />
          <SectionGroup title='Payment method'>
            <StripePaymentMethod
              paymentMethod={paymentMethod}
              onAddPaymentMethod={(paymentMethod) => setPaymentMethod(paymentMethod)}
              onRemovePaymentMethod={() => setPaymentMethod(undefined)}
            />
          </SectionGroup>
        </Stack>
      </SectionCard>
    </form>
  );
};

const CreateAccount: FC<{
  register: UseFormRegister<SignUpFormData>;
  errors: FieldErrorsImpl<DeepRequired<SignUpFormData>>;
}> = ({ register, errors }) => {
  return (
    <SectionGroup title='Create account'>
      <Stack spacing={4}>
        <FormControl isInvalid={Boolean(errors.email)}>
          <FormLabel htmlFor='email'>Email address</FormLabel>
          <Box maxW='sm'>
            <Input
              id='email'
              type='email'
              {...register('email', {
                required: 'Email is required',
              })}
            />
          </Box>
          <FormErrorMessage>
            <>{errors.email?.message}</>
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={Boolean(errors.password)}>
          <FormLabel htmlFor='password'>Password</FormLabel>
          <Box maxW='sm'>
            <Input
              id='password'
              type='password'
              {...register('password', {
                required: 'Password is required',
              })}
            />
          </Box>
          <FormErrorMessage>
            <>{errors.password?.message}</>
          </FormErrorMessage>
        </FormControl>
      </Stack>
    </SectionGroup>
  );
};

const PlanDetails: FC<{
  errors: FieldErrorsImpl<DeepRequired<SignUpFormData>>;
  setValue: UseFormSetValue<SignUpFormData>;
}> = ({ errors, setValue }) => {
  const { monthlyPrice, yearlyMonthlyPrice } = useGetPrices();

  const { recipientCount } = useSelector((state: RootState) => state.recipientCount);

  const { getRadioProps } = useRadioGroup({
    name: 'billingPeriod',
    defaultValue: 'yearly',
    onChange: (v) => setValue('billingPeriod', v as BillingPeriod, { shouldValidate: true }),
  });

  return (
    <SectionGroup title='Subscription details'>
      <Stack spacing={8}>
        <FormControl isInvalid={Boolean(errors.recipientCount)}>
          <FormLabel htmlFor='recipientCount' hidden>
            Number of recipients
          </FormLabel>
          <RecipientSlider />
          <FormErrorMessage>
            <>{errors.recipientCount?.message}</>
          </FormErrorMessage>
        </FormControl>
        <Stack spacing={4}>
          {billingPeriods.map((billingPeriod: BillingPeriod) => {
            return (
              <RadioCard
                key={billingPeriod}
                label={billingPeriod === 'monthly' ? 'Billed monthly' : 'Billed yearly'}
                info={`$${getMonthlyPrice(
                  billingPeriod,
                  recipientCount,
                  monthlyPrice,
                  yearlyMonthlyPrice
                )}/month (First month free!)`}
                {...getRadioProps({ value: billingPeriod })}
              />
            );
          })}
        </Stack>
      </Stack>
    </SectionGroup>
  );
};

const SectionFooter: FC<{ isValid: boolean; isSubmitting: boolean; charge: string }> = ({
  isValid,
  isSubmitting,
  charge,
}) => {
  return (
    <Flex justifyContent='space-between' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
      <Logo />
      <Flex alignItems='center' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
        <Stack spacing={0} textAlign={{ base: 'center', lg: 'right' }}>
          <Text fontSize='xs'>
            Subscribe today and get your <strong>first month free</strong>!
          </Text>
          <Text fontSize='xs'>
            After your first month, your card will be charged <strong>{charge}</strong> until canceled.
          </Text>
        </Stack>
        <Button isLoading={isSubmitting} disabled={!isValid || isSubmitting} type='submit'>
          Complete sign up
        </Button>
      </Flex>
    </Flex>
  );
};
